<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
    class="ad-new-assignment v-card v-sheet theme--light ma-4"
  >
    <v-tabs v-model="tab" background-color="transparent" grow>
      <v-tab v-for="step in steps" :key="step">{{ step }}</v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">

      <!-- ── Tab 1: Workflow fields ── -->
      <v-tab-item>
        <v-container>
          <v-row class="d-flex align-center justify-center">
            <v-col cols="9">
              <v-text-field outlined readonly label="Workflow Name" v-model="workflowName" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="4" class="py-0"></v-col>
            <v-col cols="12" sm="4" class="py-0">
              <!-- ✅ v-model on localItem, NOT workflowItem -->
              <v-text-field outlined label="Slack ID" v-model="localItem.slackId" />
            </v-col>
            <v-col cols="12" sm="4" class="py-0"></v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="4" class="pb-0">
              <v-autocomplete outlined label="Select Project" :items="projectsOptions" v-model="localItem.project" :rules="[rules.required]" />
            </v-col>
            <v-col cols="12" sm="4" class="pb-0">
              <v-autocomplete outlined label="Select GEO" :items="geoOptions" v-model="localItem.geo" :rules="[rules.required]" />
            </v-col>
            <v-col cols="12" sm="4" class="pb-0">
              <v-autocomplete outlined label="Select Request Type" :items="requestTypeOptions" v-model="localItem.requestType" :rules="[rules.required]" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="4" class="pb-0">
              <v-autocomplete outlined label="Select Media Type" :items="mediaTypeOptions" v-model="localItem.mediaType" />
            </v-col>
            <v-col cols="12" sm="4" class="pb-0">
              <v-text-field outlined label="Genre" v-model="localItem.genre" />
            </v-col>
            <v-col cols="12" sm="4" class="pb-0">
              <v-autocomplete outlined label="Select Keyword" :items="keywordOptions" v-model="localItem.keyword" />
            </v-col>
          </v-row>
        </v-container>
      </v-tab-item>

      <!-- ── Tab 2: Resources ── -->
      <v-tab-item eager>
        <v-container>
          <v-row class="d-flex align-center justify-center">
            <v-col cols="9">
              <v-text-field outlined readonly label="Workflow Name" v-model="workflowName" />
            </v-col>
          </v-row>
          <v-row class="d-flex align-center justify-center px-4">
            <h3 class="pb-4">Resources</h3>
            <v-tabs v-model="secondTab" centered>
              <v-tab v-for="step in secondTabSteps" :key="step" color="primary">{{ step }}</v-tab>
            </v-tabs>
            <v-tabs-items v-model="secondTab" style="width: 100% !important">

              <!-- Primary -->
              <v-tab-item>
                <v-container>
                  <v-data-table :headers="headers" :items="writerList" :items-per-page="10" :sort-by="['writerTier']" :sort-desc="[false]" class="elevation-1">
                    <template v-slot:item="{ item }">
                      <tr>
                        <td style="width: 40%">{{ item.name }}</td>
                        <td>
                          <v-btn
                            :class="{'blue lighten-4': item.writerTier === 'P2', 'green lighten-4': item.writerTier === 'P1'}"
                            small rounded variant="outlined"
                            @click.prevent="() => { item.writerTier = item.writerTier === 'P2' ? 'P1' : 'P2'; }"
                          >{{ item.writerTier }}</v-btn>
                        </td>
                        <td>{{ item.lastPrimaryAssignmentDate }}</td>
                        <td>
                          <v-btn height="36px" small color="secondary" @click.prevent="removeItem(item, 'writerList')">
                            <v-icon>mdi-cancel</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </template>
                    <template v-slot:body.append>
                      <tr v-if="removedWritersList.length">
                        <td>
                          <v-autocomplete style="margin-bottom: -27px" outlined dense label="Primary" :items="removedWritersNamesList" v-model="removedWriter" />
                        </td>
                        <td colspan="2">
                          <v-btn v-if="removedWriter" class="green lighten-3" small rounded variant="outlined" @click.prevent="addItem('writerList')">Add</v-btn>
                        </td>
                        <td></td><td></td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>

              <!-- Secondary -->
              <v-tab-item>
                <v-container>
                  <v-data-table :headers="editorHeaders" :items="editorList" :items-per-page="10" :sort-by="['editorTier']" :sort-desc="[false]" class="elevation-1">
                    <template v-slot:item="{ item }">
                      <tr>
                        <td style="width: 40%">{{ item.name }}</td>
                        <td>
                          <v-btn
                            :class="{'blue lighten-4': item.editorTier === 'S2', 'green lighten-4': item.editorTier === 'S1'}"
                            small rounded variant="outlined"
                            @click.prevent="() => { item.editorTier = item.editorTier === 'S2' ? 'S1' : 'S2'; }"
                          >{{ item.editorTier }}</v-btn>
                        </td>
                        <td>{{ item.lastSecondaryAssignmentDate }}</td>
                        <td>
                          <v-btn height="36px" small color="secondary" @click.prevent="removeItem(item, 'editorList')">
                            <v-icon>mdi-cancel</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </template>
                    <template v-slot:body.append>
                      <tr v-if="removedEditorsList.length">
                        <td>
                          <v-autocomplete style="margin-bottom: -27px" outlined dense label="Secondary" :items="removedEditorsNamesList" v-model="removedEditor" />
                        </td>
                        <td colspan="2">
                          <v-btn v-if="removedEditor" class="green lighten-3" small rounded variant="outlined" @click.prevent="addItem('editorList')">Add</v-btn>
                        </td>
                        <td></td><td></td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>

              <!-- QC -->
              <v-tab-item>
                <v-container>
                  <v-data-table :headers="qcHeaders" :items="qcList" :items-per-page="10" :sort-by="['qcTier']" :sort-desc="[false]" class="elevation-1">
                    <template v-slot:item="{ item }">
                      <tr>
                        <td style="width: 40%">{{ item.name }}</td>
                        <td>
                          <v-btn
                            :class="{'blue lighten-4': item.qcTier === 'Q2', 'green lighten-4': item.qcTier === 'Q1'}"
                            small rounded variant="outlined"
                            @click.prevent="() => { item.qcTier = item.qcTier === 'Q2' ? 'Q1' : 'Q2'; }"
                          >{{ item.qcTier }}</v-btn>
                        </td>
                        <td>{{ item.lastQcAssignmentDate }}</td>
                        <td>
                          <v-btn height="36px" small color="secondary" @click.prevent="removeItem(item, 'qcList')">
                            <v-icon>mdi-cancel</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </template>
                    <template v-slot:body.append>
                      <tr v-if="removedQcList.length">
                        <td>
                          <v-autocomplete style="margin-bottom: -27px" outlined dense label="QC" :items="removedQcNamesList" v-model="removedQc" />
                        </td>
                        <td colspan="2">
                          <v-btn v-if="removedQc" class="green lighten-3" small rounded variant="outlined" @click.prevent="addItem('qcList')">Add</v-btn>
                        </td>
                        <td></td><td></td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>

            </v-tabs-items>
          </v-row>
        </v-container>
      </v-tab-item>

      <!-- ── Tab 3: History ── -->
      <v-tab-item eager>
        <v-container class="d-flex" ref="parentDiv">
          <div class="timeline__left">
            <v-timeline align-top dense>
              <v-timeline-item
                color="green lighten-1" icon="mdi-pencil" class="timeline__item"
                v-for="(item, n) in workflowHistoryPrepared" :key="n"
              >
                <v-row class="pt-2" @click.stop="toggleHistoryItem(item)">
                  <v-col cols="3"><strong>{{ item.updatedOn[1] }}/{{ item.updatedOn[2] }}/{{ item.updatedOn[0] }}</strong></v-col>
                  <v-col><strong>Updated by {{ item.userName }}</strong></v-col>
                </v-row>
              </v-timeline-item>
              <v-timeline-item color="teal lighten-3" class="timeline__item" icon="mdi-plus" v-if="workflowCreatedHistoryItem.length">
                <v-row class="pt-2">
                  <v-col cols="3"><strong>{{ workflowCreatedHistoryItem[0]?.updatedOn[1] }}/{{ workflowCreatedHistoryItem[0]?.updatedOn[2] }}/{{ workflowCreatedHistoryItem[0]?.updatedOn[0] }}</strong></v-col>
                  <v-col><strong>Created by {{ workflowCreatedHistoryItem[0]?.userName }}</strong></v-col>
                </v-row>
              </v-timeline-item>
              <v-timeline-item color="teal lighten-3" class="timeline__item" icon="mdi-plus" v-if="!workflowCreatedHistoryItem.length">
                <v-row class="pt-2">
                  <v-col cols="3"></v-col>
                  <v-col><strong>Will be created by {{ user.user.firstName }} {{ user.user.lastName }}</strong></v-col>
                </v-row>
              </v-timeline-item>
            </v-timeline>
          </div>
          <div class="timeline__right" ref="childDiv">
            <transition name="fade">
              <ul v-if="showItem">
                <li v-for="(item, n) in workflowMessage" :key="n">{{ item }}</li>
              </ul>
            </transition>
          </div>
        </v-container>
      </v-tab-item>

    </v-tabs-items>
  </v-form>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { getStaffCapacity } from "@/utils/newDbUtils";

export default {
  name: "WorkflowForm",
  props: {
    workflowItem: Object,
    datasIndex: Number,
  },
  data() {
    return {
      localItem: null,
      showItem: false,
      valid: true,
      steps: ["Workflow", "Resources", "History"],
      tab: 0,
      secondTab: 0,
      secondTabSteps: ["Primary", "Secondary", "QC"],
      rules: {
        required: (value) => !!value || value === 0 || "Required",
      },
      resourcesOptions: [],
      headers: [
        { text: "Name", align: "left", sortable: true, value: "name" },
        { text: "Tier", value: "writerTier", sortable: true, align: "left" },
        { text: "Last Assigned", value: "lastPrimaryAssignmentDate", sortable: false, align: "left" },
        { text: "Remove", value: "actions", sortable: false, align: "left" },
      ],
      editorHeaders: [
        { text: "Name", align: "left", sortable: true, value: "name" },
        { text: "Tier", value: "editorTier", sortable: true, align: "left" },
        { text: "Last Assigned", value: "lastSecondaryAssignmentDate", sortable: false, align: "left" },
        { text: "Remove", value: "actions", sortable: false, align: "left" },
      ],
      qcHeaders: [
        { text: "Name", align: "left", sortable: true, value: "name" },
        { text: "Tier", value: "qcTier", sortable: true, align: "left" },
        { text: "Last Assigned", value: "lastQcAssignmentDate", sortable: false, align: "left" },
        { text: "Remove", value: "actions", sortable: false, align: "left" },
      ],
      writerList: [],
      removedWritersList: [],
      removedWriter: null,
      editorList: [],
      removedEditorsList: [],
      removedEditor: null,
      qcList: [],
      removedQcList: [],
      removedQc: null,
      workflowName: "",
      workflowMessage: null,
    };
  },
  created() {
    // ✅ Deep clone on creation — localItem is fully independent from the store
    this.localItem = _.cloneDeep(this.workflowItem);
    const nameList = [
      this.localItem.project, this.localItem.geo, this.localItem.requestType,
      this.localItem.mediaType, this.localItem.genre, this.localItem.keyword,
    ];
    this.workflowName = nameList.filter(Boolean).join("_");
  },
  computed: {
    ...mapGetters("workflow", ["gWorkflow", "gWorkflowData", "gIsUpdated", "gNewWorkflowsDatas"]),
    ...mapGetters("assignment", ["projectsList", "geoOptions", "requestTypeOptions", "mediaTypeOptions", "keywordOptions"]),
    ...mapGetters("staff", ["gStaffList"]),
    ...mapGetters("auth", ["user"]),
    workflowHistoryPrepared() {
      const result = [];
      this.localItem?.groupedWorkflowHistory?.forEach((history) => {
        if (history[0].updateStatus === "UPDATED") {
          let notes = [];
          history.forEach((el) => { notes = [...notes, ...el.comments]; });
          const staff = this.gStaffList.find((s) => s.user.id === history[0].updatedBy);
          result.push({ ...history[0], notes, userName: `${staff?.user.firstName} ${staff?.user.lastName}` });
        }
      });
      return result;
    },
    workflowCreatedHistoryItem() {
      const result = [];
      this.localItem?.groupedWorkflowHistory?.forEach((history) => {
        if (history[0].updateStatus === "CREATED") {
          let notes = [];
          history.forEach((el) => { notes = [...notes, ...el.comments]; });
          const staff = this.gStaffList.find((s) => s.user.id === history[0].updatedBy);
          result.push({ ...history[0], notes, userName: `${staff?.user.firstName} ${staff?.user.lastName}` });
        }
      });
      return result;
    },
    projectsOptions() {
      return this.projectsList.map((p) => p.name);
    },
    removedWritersNamesList() {
      return this.removedWritersList.map((s) => s.name);
    },
    removedEditorsNamesList() {
      return this.removedEditorsList.map((s) => s.name);
    },
    removedQcNamesList() {
      return this.removedQcList.map((s) => s.name);
    },
  },
  watch: {
    // ✅ Watch localItem fields, not workflowItem
    "localItem.geo": {
      immediate: true,
      handler() { this.updateResourcesValues(); },
    },
    "localItem.project": {
      immediate: true,
      handler() { this.updateResourcesValues(); },
    },
    localItem: {
      deep: true,
      immediate: false,
      handler() {
        const nameList = [
          this.localItem.project, this.localItem.geo, this.localItem.requestType,
          this.localItem.mediaType, this.localItem.genre, this.localItem.keyword,
        ];
        this.workflowName = nameList.filter(Boolean).join("_");
      },
    },
  },
  methods: {
    ...mapActions("workflow", ["aClearWorkflow", "aSetIfUpdated", "aUpdateWorkflow"]),
    ...mapActions("flashMessage", ["handleFlash"]),

    /**
     * ✅ Called by WorkflowModal just before saving to DB.
     * Writes localItem + resource lists back into Vuex so
     * prepareDataToCreateWorkflow / prepareDataToUpdateWorkflow see fresh data.
     */
    flushLocalToStore() {
      this.aUpdateWorkflow({
        ...this.localItem,
        name: this.workflowName,
        primary: this.writerList,
        secondary: this.editorList,
        qc: this.qcList,
      });
    },

    async updateResourcesValues() {
      if (!this.localItem) return;
      const project = this.projectsList.find((el) => el.name === this.localItem.project);
      if (this.localItem.geo && project && project.staff.length) {
        this.resourcesOptions = project.staff.filter(
          (el) => el.adminDetails?.geos?.length && el.adminDetails.geos.includes(this.localItem.geo)
        );
        this.writerList = [];
        this.editorList = [];
        this.qcList = [];

        this.resourcesOptions.forEach((staff) => {
          const staffData = {
            name: `${staff.user.firstName} ${staff.user.lastName}`,
            userId: staff.user.id,
            writerTier: "", editorTier: "", qcTier: "",
            lastPrimaryAssignmentDate: "",
          };
          if (this.removedWritersList.findIndex((el) => el.name === staffData.name) === -1) {
            this.writerList.push({ ...staffData, writerTier: "P2" });
          }
          if (this.removedEditorsList.findIndex((el) => el.name === staffData.name) === -1) {
            this.editorList.push({ ...staffData, editorTier: "S2" });
          }
          if (this.removedQcList.findIndex((el) => el.name === staffData.name) === -1) {
            this.qcList.push({ ...staffData, qcTier: "Q2" });
          }
        });

        if (this.localItem.primary?.length) {
          const existedItems = [];
          this.localItem.primary.forEach((item) => {
            const idx = this.writerList.findIndex((el) => el.name === item.name);
            if (idx !== -1) { this.writerList.splice(idx, 1); existedItems.push(item); }
          });
          this.removedWritersList = this.writerList;
          this.writerList = existedItems;
        }
        if (this.localItem.secondary?.length) {
          const existedItems = [];
          this.localItem.secondary.forEach((item) => {
            const idx = this.editorList.findIndex((el) => el.name === item.name);
            if (idx !== -1) { this.editorList.splice(idx, 1); existedItems.push(item); }
          });
          this.removedEditorsList = this.editorList;
          this.editorList = existedItems;
        }
        if (this.localItem.qc?.length) {
          const existedItems = [];
          this.localItem.qc.forEach((item) => {
            const idx = this.qcList.findIndex((el) => el.name === item.name);
            if (idx !== -1) { this.qcList.splice(idx, 1); existedItems.push(item); }
          });
          this.removedQcList = this.qcList;
          this.qcList = existedItems;
        }
        if (this.gIsUpdated && !this.localItem.qc?.length) {
          if (this.qcList.length) this.removedQcList = _.cloneDeep(this.qcList);
          this.qcList = [];
        }

        const data = {
          idList: [...this.writerList, ...this.editorList, ...this.qcList].map((el) => el.userId),
          selectedDate: this.$moment().format("YYYY-MM-DD"),
        };
        if (data.idList.length) {
          const result = await getStaffCapacity(data);
          const cap = result.data;
          this.writerList.forEach((el) => {
            const d = cap.find((c) => c.id === el.userId)?.lastPrimaryAssignmentDate;
            if (d) el.lastPrimaryAssignmentDate = `${d[0]}-${d[1]}-${d[2]}`;
          });
          this.editorList.forEach((el) => {
            const d = cap.find((c) => c.id === el.userId)?.lastSecondaryAssignmentDate;
            if (d) el.lastSecondaryAssignmentDate = `${d[0]}-${d[1]}-${d[2]}`;
          });
          this.qcList.forEach((el) => {
            const d = cap.find((c) => c.id === el.userId)?.lastQcAssignmentDate;
            if (d) el.lastQcAssignmentDate = `${d[0]}-${d[1]}-${d[2]}`;
          });
        }
      } else {
        this.writerList = [];
        this.editorList = [];
        this.qcList = [];
      }
    },

    showHistoryItem(item) { this.workflowMessage = item.notes; this.showItem = true; },
    hideHistoryItem() { this.workflowMessage = []; this.showItem = false; },
    toggleHistoryItem(item) { this.showItem ? this.hideHistoryItem() : this.showHistoryItem(item); },
    validateForm() { return this.$refs.form.validate(); },

    removeItem(item, list) {
      if (list === "writerList") {
        const idx = this.writerList.findIndex((el) => el.name === item.name);
        this.removedWritersList.push(this.writerList.splice(idx, 1)[0]);
      } else if (list === "editorList") {
        const idx = this.editorList.findIndex((el) => el.name === item.name);
        this.removedEditorsList.push(this.editorList.splice(idx, 1)[0]);
      } else if (list === "qcList") {
        const idx = this.qcList.findIndex((el) => el.name === item.name);
        this.removedQcList.push(this.qcList.splice(idx, 1)[0]);
      }
    },
    addItem(list) {
      if (list === "writerList") {
        const idx = this.removedWritersList.findIndex((el) => el.name === this.removedWriter);
        if (idx !== -1) { this.writerList.push(this.removedWritersList.splice(idx, 1)[0]); this.removedWriter = null; }
      } else if (list === "editorList") {
        const idx = this.removedEditorsList.findIndex((el) => el.name === this.removedEditor);
        if (idx !== -1) { this.editorList.push(this.removedEditorsList.splice(idx, 1)[0]); this.removedEditor = null; }
      } else if (list === "qcList") {
        const idx = this.removedQcList.findIndex((el) => el.name === this.removedQc);
        if (idx !== -1) { this.qcList.push(this.removedQcList.splice(idx, 1)[0]); this.removedQc = null; }
      }
    },

    setMaxHeight() {
      if (this.$refs.parentDiv && this.$refs.childDiv) {
        this.$refs.childDiv.style.maxHeight = `${this.$refs.parentDiv.clientHeight - 40}px`;
      }
    },
  },
  mounted() {
    this.aClearWorkflow();
    this.setMaxHeight();
    window.addEventListener("resize", this.setMaxHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.setMaxHeight);
  },
};
</script>

<style lang="scss" scoped>
.v-tab { min-width: 0 !important; }
.timeline {
  &__left { width: 50% !important; }
  &__right {
    position: fixed;
    left: 50%;
    padding: 15px 0;
    max-height: 111px;
    overflow-y: auto;
  }
}
.timeline__item:hover { cursor: pointer; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.7s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
